<script setup lang="ts">
import type { ResolvedMonospaceApiKey } from '#layers/data-model/public';
import type { DialogEmits } from '#layers/ui/public';
import { useEmitAsProps } from 'reka-ui';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as z from 'zod/v4-mini';
import { useApiKeyGeneration } from '#layers/access/composables/use-api-key-generation';
import { useLicenseEntitlementLimit } from '#layers/access/composables/use-license-entitlement-limit';
import { useServiceAccountCreation } from '#layers/access/composables/use-service-account-creation';
import { useServiceAccountOptions } from '#layers/access/composables/use-service-account-options';
import { DEFAULT_TTL, TTL_OPTIONS } from '#layers/access/constants/api-keys';
import { nonEmptyString } from '#layers/base/public';
import { useSelectOptions } from '#layers/block-engine/public';
import { useSystemEntityStore } from '#layers/runtime/public';
import { useToastResultHandling } from '#layers/ui/public';

const { apiKey, userId } = defineProps<{
	userId: string | null;
	apiKey?: ResolvedMonospaceApiKey;
}>();

const emit = defineEmits<DialogEmits>();

const forwarded = useEmitAsProps(emit);
const { t } = useI18n();

const isEditMode = computed(() => !!apiKey);

const createApiKeySchema = z.object({
	userId: nonEmptyString(),
	name: nonEmptyString(),
	ttl: z.nullable(nonEmptyString()),
	description: z.optional(z.string()),
});

const editApiKeySchema = z.object({
	name: nonEmptyString(),
	description: z.optional(z.string()),
});

const store = useSystemEntityStore();

const { isGenerating, generatedToken: createdToken, generate } = useApiKeyGeneration();
const { normalizedOptions: ttlOptions } = useSelectOptions(() => TTL_OPTIONS);

const { onSuccess: onEditSuccess, onError: onEditError } = useToastResultHandling({
	success: {
		title: t('api_keys.edit_success'),
	},
	error: {
		title: t('api_keys.edit_error'),
	},
});

const { guardCreation } = useLicenseEntitlementLimit({ scope: 'system' });

const phase = ref<'form' | 'success'>('form');

interface ApiKeyFormState {
	userId: string;
	name: string;
	ttl: string | null;
	description: string;
}

// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- seeds the form from the key this dialog opened for; edits must not be overwritten
const formState = reactive<ApiKeyFormState>({
	userId: apiKey?.user?.id ?? userId ?? '',
	name: apiKey?.name ?? '',
	ttl: DEFAULT_TTL,
	description: apiKey?.description ?? '',
});

const showServiceAccountSelect = computed(() => !userId && !isEditMode.value);

const {
	isLoading: serviceAccountsLoading,
	refresh: refreshServiceAccounts,
	searchTerm: serviceAccountSearchTerm,
	serviceAccountOptions,
} = useServiceAccountOptions({
	selectedId: () => (showServiceAccountSelect.value && formState.userId) || undefined,
});

const hasNoServiceAccounts = computed(() => showServiceAccountSelect.value
	&& !serviceAccountsLoading.value
	&& serviceAccountSearchTerm.value.trim() === ''
	&& serviceAccountOptions.value.length === 0);

const { isCreating: isCreatingServiceAccount, createServiceAccount } = useServiceAccountCreation({
	select: (serviceAccount) => {
		// Selecting also turns the "no service accounts" notice back into the form.
		formState.userId = serviceAccount.id;
	},
	refresh: refreshServiceAccounts,
});

const isSubmitting = ref(false);

const title = computed(() => {
	if (phase.value === 'success') return t('api_keys.created_title');
	return isEditMode.value ? t('api_keys.edit_title') : t('api_keys.create_title');
});

const confirm = computed(() => {
	if (hasNoServiceAccounts.value) {
		return {
			label: t('service_accounts.create_service_account'),
			icon: 'lucide:plus',
			loading: isCreatingServiceAccount.value,
			onClick: handleCreateServiceAccount,
		};
	}

	if (phase.value === 'success') {
		return {
			label: t('common.done'),
		};
	}

	return {
		label: isEditMode.value ? t('common.save') : t('common.create'),
		loading: isSubmitting.value || isGenerating.value,
		type: 'submit' as const,
		form: 'api-key-form',
		onClick: handleSubmit,
	};
});

const cancel = computed(() => {
	if (phase.value === 'success') {
		return false;
	}

	return true;
});

async function handleCreateServiceAccount() {
	const canCreate = await guardCreation('service_accounts');
	if (canCreate) await createServiceAccount();
}

async function handleEdit() {
	if (!apiKey || isSubmitting.value) return;

	isSubmitting.value = true;

	try {
		await store.MonospaceApiKey.update({
			name: formState.name,
			description: formState.description || null,
		}, { key: apiKey.id });

		onEditSuccess();
		emit('close', true);
	}
	catch (error) {
		onEditError(error);
	}
	finally {
		isSubmitting.value = false;
	}
}

async function handleCreate() {
	const result = await generate({
		userId: formState.userId,
		name: formState.name,
		ttl: formState.ttl,
		description: formState.description || undefined,
	});
	if (!result) return;

	phase.value = 'success';
}

function handleSubmit() {
	return isEditMode.value ? handleEdit() : handleCreate();
}

function handleClose() {
	emit('close', phase.value === 'success');
}
</script>

<template>
	<DDialog
		v-bind="forwarded"
		:title
		:confirm
		:cancel
		@after:leave="handleClose"
	>
		<template #body>
			<!-- No Service Accounts State -->
			<DNotice
				v-if="hasNoServiceAccounts"
				color="warning"
				:title="$t('api_keys.no_service_accounts_title')"
				:description="$t('api_keys.no_service_accounts_description')"
			/>

			<DForm
				v-else-if="phase === 'form'"
				id="api-key-form"
				:state="formState"
				:schema="isEditMode ? editApiKeySchema : createApiKeySchema"
				layout="stacked"
				auto-focus-first-input
			>
				<DFormField
					v-if="showServiceAccountSelect"
					name="userId"
					:label="$t('api_keys.service_account')"
					required
				>
					<DSelectMenu
						v-model="formState.userId"
						v-model:search-term="serviceAccountSearchTerm"
						value-key="id"
						:items="serviceAccountOptions"
						:loading="serviceAccountsLoading"
						:placeholder="$t('api_keys.select_service_account')"
						:multiple="false"
						ignore-filter
					/>
				</DFormField>

				<DFormField
					name="name"
					:label="$t('common.name')"
					required
					autocomplete="off"
				>
					<DInput
						v-model.trim="formState.name"
						:placeholder="$t('api_keys.name_placeholder')"
					/>
				</DFormField>

				<DFormField
					v-if="!isEditMode"
					name="ttl"
					:label="$t('api_keys.ttl')"
					required
				>
					<DSelect
						v-model="formState.ttl"
						:items="ttlOptions"
					/>
				</DFormField>

				<DNotice
					v-if="formState.ttl === null"
					class="col-span-full"
					color="warning"
					:title="$t('expiry.never')"
					:description="$t('api_keys.ttl_never_description')"
				/>

				<DFormField
					name="description"
					:label="$t('common.description')"
				>
					<DTextarea
						v-model.trim="formState.description"
						:placeholder="$t('api_keys.description_placeholder')"
					/>
				</DFormField>
			</DForm>

			<!-- Success Phase -->
			<div v-else class="flex flex-col gap-4">
				<DNotice
					color="warning"
					:title="$t('api_keys.token_warning_title')"
					:description="$t('api_keys.token_warning_description')"
				/>

				<div class="flex flex-col gap-2">
					<div class="flex gap-2">
						<DTextarea
							:aria-label="$t('common.api_key')"
							:model-value="createdToken ?? ''"
							readonly
							class="flex-1 resize-none font-mono"
							:rows="8"
							copy
						/>
					</div>
				</div>
			</div>
		</template>
	</DDialog>
</template>
